import { AuditLogEntityName } from "@/api-schema-enums";
import { useAuditLogSearchQuery } from "@/common/api/audit-log/use-audit-log-search.query";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Skeleton } from "@/components/ui/skeleton";
import { keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AuditLogTimelineEntry } from "./audit-log-timeline-entry";

const AUDIT_LOG_PAGE_SIZE = 50;
const AUDIT_LOG_MAX_PAGE_SIZE = 1000; // matches the API's MAX_PAGE_SIZE

interface AuditLogCardProps {
  entityName: AuditLogEntityName;
  uuid: string;
}

export const AuditLogTimeline = ({ entityName, uuid, enabled }: AuditLogCardProps & { enabled: boolean }) => {
  const { t } = useTranslation("common");
  const [pageSize, setPageSize] = useState(AUDIT_LOG_PAGE_SIZE);
  const { data, isLoading, isError, isFetching } = useAuditLogSearchQuery({
    options: { parameters: { pageSize }, body: { entityName: [entityName], entityId: [uuid] } },
    enabled: enabled && !!uuid,
    queryOptions: { placeholderData: keepPreviousData },
  });
  const entries = data?.data ?? [];
  const hasMore = entries.length < (data?.meta.total ?? 0);
  const reachedMaxPageSize = pageSize >= AUDIT_LOG_MAX_PAGE_SIZE;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-3/5" />
      </div>
    );
  }

  if (isError) {
    return <p className="text-error text-sm">{t("common:ErrorLoadingData")}</p>;
  }

  if (entries.length === 0) {
    return <p className="text-muted-foreground text-sm">{t("common:AuditLogEmpty")}</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <ol>
        {entries.map((entry) => (
          <AuditLogTimelineEntry key={entry.uuid} entry={entry} />
        ))}
      </ol>
      {hasMore && (
        <Button
          variant={ButtonVariant.Outlined}
          colorVariant={ButtonColorVariant.Secondary}
          size={ButtonSize.Sm}
          className="self-center"
          disabled={isFetching || reachedMaxPageSize}
          onClick={() => setPageSize((previous) => Math.min(previous + AUDIT_LOG_PAGE_SIZE, AUDIT_LOG_MAX_PAGE_SIZE))}
        >
          {isFetching && <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />}
          {t("common:LoadMore")}
        </Button>
      )}
    </div>
  );
};
